import { QueryClient } from '@tanstack/vue-query';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { get, set, del } from 'idb-keyval';
import packageJson from '/package.json';

const CACHE_KEY = 'jmpr_query_cache';

/**
 * Platform-aware persister:
 * - Native (iOS/Android): Capacitor Preferences → UserDefaults (iOS) / SharedPreferences (Android).
 *   These are NOT inherently encrypted. Data is stored in app-sandboxed files accessible only to
 *   this app. For full encryption at rest, a secure/encrypted storage plugin would be required.
 * - Web: IndexedDB via idb-keyval — not plaintext, requires async API call (harder XSS target
 *   than localStorage); per-origin isolated; not accessible via document.cookie exfiltration.
 *
 * @type {import('@tanstack/query-persist-client-core').Persister}
 */
const persister = Capacitor.isNativePlatform()
  ? {
      persistClient: async (client) => {
        try {
          await Preferences.set({ key: CACHE_KEY, value: JSON.stringify(client) });
        } catch (e) {
          console.warn('[QueryClient] Failed to persist cache to Capacitor Preferences:', e);
        }
      },
      restoreClient: async () => {
        try {
          const { value } = await Preferences.get({ key: CACHE_KEY });
          return value ? JSON.parse(value) : undefined;
        } catch (e) {
          console.warn('[QueryClient] Failed to restore cache from Capacitor Preferences:', e);
          return undefined;
        }
      },
      removeClient: () => Preferences.remove({ key: CACHE_KEY }),
    }
  : {
      persistClient: async (client) => {
        try {
          await set(CACHE_KEY, client);
        } catch (e) {
          console.warn('[QueryClient] Failed to persist cache to IndexedDB:', e);
        }
      },
      restoreClient: async () => {
        try {
          return await get(CACHE_KEY);
        } catch (e) {
          console.warn('[QueryClient] Failed to restore cache from IndexedDB:', e);
          return undefined;
        }
      },
      removeClient: () => del(CACHE_KEY),
    };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Query keys that must never be written to (or restored from) the persisted
// cache. Connection verification status has to be live on every load, so it is
// excluded from persistence entirely — see useConnectionVerification.ts.
const NON_PERSISTED_QUERY_KEYS = ['connection-verify'];

persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  // The '-v2' suffix busts any persisted cache written before the TanStack
  // migration (APICC-2233) that stored raw API envelopes instead of the
  // unwrapped payload. Removing this suffix in a future release is safe.
  buster: packageJson.version + '-v2',
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      !NON_PERSISTED_QUERY_KEYS.includes(query.queryKey?.[0]),
  },
});

// Connect to TanStack Query DevTools
window.__TANSTACK_QUERY_CLIENT__ = queryClient;

/** Clears both the in-memory QueryClient and the persisted cache. */
export const clearQueryCache = async () => {
  queryClient.clear();
  await persister.removeClient();
};

export const getQueryClient = () => queryClient;
